/** Shapes shared by the server, the checkout app and the offline cache. */
import type { Minor } from './money.js';
import type { Discount, PaymentMethod } from './sale.js';
import type { Permission } from './permissions.js';

/**
 * A role is just a name attached to a set of permissions. 'owner', 'manager' and
 * 'cashier' are the three the shop starts with; the owner can add more.
 */
export type Role = string;

export interface RoleRecord {
  id: string;
  key: string;
  nameEn: string;
  nameAr: string;
  builtIn: boolean;
  permissions: Permission[];
  maxDiscountPercent: number;
  /** How many people currently hold this role. */
  userCount?: number;
}
export type Lang = 'en' | 'ar';

export interface Bilingual {
  en: string;
  ar: string;
}

/** Show the user's language; fall back to the other one rather than showing nothing. */
export function pickLang(b: { en?: string | null; ar?: string | null } | null | undefined, lang: Lang): string {
  if (!b) return '';
  const first = lang === 'ar' ? b.ar : b.en;
  const second = lang === 'ar' ? b.en : b.ar;
  return (first && first.trim()) || (second && second.trim()) || '';
}

export interface User {
  id: string;
  username: string;
  name: string;
  /** The role key, e.g. 'cashier' or a custom one the owner made. */
  role: Role;
  roleId?: string | null;
  roleNameEn?: string;
  roleNameAr?: string;
  lang: Lang;
  active: boolean;
  maxDiscountPercent: number;
  /** Everything this person can actually do, role plus per-person changes. */
  permissions: Permission[];
}

export interface Device {
  id: string;
  name: string;
  seriesPrefix: string;
  approved: boolean;
  lastSeenAt?: string | null;
}

export interface SizeScale {
  id: string;
  nameEn: string;
  nameAr: string;
  sizes: string[];
  kind: 'letter' | 'numeric' | 'kids' | 'shoes' | 'one' | 'custom';
}

export interface Color {
  id: string;
  nameEn: string;
  nameAr: string;
  hex: string;
}

export interface Category {
  id: string;
  nameEn: string;
  nameAr: string;
  parentId?: string | null;
}

export interface Brand {
  id: string;
  nameEn: string;
  nameAr: string;
}

export interface Supplier {
  id: string;
  name: string;
  phone?: string | null;
  notes?: string | null;
}

export interface Product {
  id: string;
  sku: string;
  nameEn: string;
  nameAr: string;
  categoryId?: string | null;
  brandId?: string | null;
  supplierId?: string | null;
  season?: string | null;
  sizeScaleId?: string | null;
  costPrice: Minor;
  salePrice: Minor;
  imageUrl?: string | null;
  finalSale: boolean;
  active: boolean;
  createdAt: string;
}

export interface Variant {
  id: string;
  productId: string;
  size: string;
  colorId?: string | null;
  sku: string;
  priceOverride?: Minor | null;
  avgCost: Minor;
  stock: number;
  lowStockThreshold: number;
  active: boolean;
  barcodes: string[];
}

/** What the checkout needs about an item; flattened so the offline cache stays small. */
export interface CatalogItem {
  variantId: string;
  productId: string;
  sku: string;
  nameEn: string;
  nameAr: string;
  size: string;
  colorEn: string;
  colorAr: string;
  colorHex: string;
  price: Minor;
  avgCost: Minor;
  stock: number;
  categoryId?: string | null;
  categoryEn?: string;
  categoryAr?: string;
  brandId?: string | null;
  brandEn?: string;
  brandAr?: string;
  finalSale: boolean;
  /** The price is typed at the till, and no stock moves. A miscellaneous item. */
  openPrice?: boolean;
  barcodes: string[];
  lowStockThreshold: number;
  imageUrl?: string | null;
}

export type StockReason =
  | 'receive'
  | 'sale'
  | 'return'
  | 'exchange_in'
  | 'exchange_out'
  | 'adjust_damaged'
  | 'adjust_lost'
  | 'adjust_count'
  | 'adjust_sample'
  | 'adjust_other'
  | 'count'
  | 'void';

export interface StockMovement {
  id: string;
  variantId: string;
  qty: number;
  reason: StockReason;
  refType?: string | null;
  refId?: string | null;
  unitCost?: Minor | null;
  userId?: string | null;
  deviceId?: string | null;
  note?: string | null;
  createdAt: string;
}

export type SaleStatus = 'completed' | 'voided' | 'held';
export type SaleKind = 'sale' | 'exchange' | 'return';

export interface SaleLineRecord {
  id: string;
  saleId: string;
  variantId: string;
  /** Copied onto the line so an old receipt still reads correctly if a product is renamed. */
  nameEn: string;
  nameAr: string;
  size: string;
  colorEn: string;
  colorAr: string;
  sku: string;
  qty: number;
  unitPrice: Minor;
  discount: Discount;
  lineDiscount: Minor;
  saleDiscountShare: Minor;
  net: Minor;
  netUnit: Minor;
  unitCost: Minor;
  finalSale: boolean;
  returnedQty: number;
  isReturn: boolean;
}

export interface PaymentRecord {
  id: string;
  saleId: string;
  method: PaymentMethod;
  methodLabel?: string | null;
  currency: string;
  amount: Minor;
  rateMicros: number;
  decimals: number;
  amountIls: Minor;
  reference?: string | null;
}

export interface SaleRecord {
  id: string;
  receiptNo: string;
  kind: SaleKind;
  status: SaleStatus;
  deviceId: string;
  deviceCounter: number;
  userId: string;
  shiftId?: string | null;
  customerId?: string | null;
  originalSaleId?: string | null;
  gross: Minor;
  discountTotal: Minor;
  total: Minor;
  rounding: Minor;
  payable: Minor;
  changeIls: Minor;
  vatRate: number;
  vatAmount: Minor;
  netAmount: Minor;
  costTotal: Minor;
  itemCount: number;
  note?: string | null;
  createdAt: string;
  voidedAt?: string | null;
  voidReason?: string | null;
  lines: SaleLineRecord[];
  payments: PaymentRecord[];
}

export interface Shift {
  id: string;
  userId: string;
  deviceId: string;
  openedAt: string;
  closedAt?: string | null;
  openingCash: Record<string, Minor>;
  closingCash?: Record<string, Minor> | null;
  status: 'open' | 'closed';
  note?: string | null;
}

export interface Customer {
  id: string;
  name: string;
  phone?: string | null;
  notes?: string | null;
  tabBalance: Minor;
}

export interface StoreCredit {
  id: string;
  code: string;
  customerId?: string | null;
  issued: Minor;
  used: Minor;
  expiresAt?: string | null;
  createdAt: string;
  sourceSaleId?: string | null;
}

export interface StoreSettings {
  storeNameEn: string;
  storeNameAr: string;
  address: string;
  phone: string;
  taxNumber: string;
  logoDataUrl?: string | null;
  vatRate: number;
  cashStepAgorot: number;
  receiptLang: 'en' | 'ar' | 'both';
  receiptFooterEn: string;
  receiptFooterAr: string;
  exchangeWindowDays: number;
  allowCashRefund: boolean;
  noReceiptLookbackDays: number;
  enforceFinalSale: boolean;
  maxCashierDiscountPercent: number;
  lowStockDefault: number;
  /** 'auto' follows each device's own light/dark setting. */
  theme: 'auto' | 'light' | 'dark';
  /** The shop's colour, as a hex. Every other shade is worked out from it. */
  accent: string;
  deadStockDays: number;
  customerTabsEnabled: boolean;
  /**
   * Counting the drawer open and closed, with a Z report at the end of the day.
   * A one-person shop that never hands the till over does not need it, so it can
   * be switched off - the screens and the reports stay, they just go quiet.
   */
  shiftsEnabled: boolean;
  /** Parking a sale to serve the next customer, and picking it up again. */
  holdSalesEnabled: boolean;
  arabicDigits: boolean;
  unsyncedWarnHours: number;
  /**
   * Sign a till out after this many minutes with nobody touching it. 0 means
   * never: a till behind the counter is usually the shop's, not one person's,
   * and signing it out mid-afternoon only makes the next customer wait.
   */
  idleSignOutMinutes: number;
  /**
   * False only for a shop created after setup existed and not yet set up: its
   * owner is walked through name, logo, receipts and look on first sign-in.
   * Shops from before default to true and never see it.
   */
  setupDone: boolean;
  paymentMethods: { key: string; labelEn: string; labelAr: string; kind: PaymentMethod }[];
  /**
   * How this shop prints. 'window': the browser's print window, which asks
   * every time. 'helper': silently, to the printers named below, through the
   * print helper on the till PC (tools/print-helper).
   */
  printVia: 'window' | 'helper';
  /** Windows' name for the receipt printer, as the helper lists it. */
  receiptPrinter: string;
  receiptPaper: '80' | '58';
  /** Print the receipt as soon as a sale is paid, instead of waiting for the button. */
  autoPrintReceipt: boolean;
  /** Windows' name for the label printer. Empty: labels use the print window. */
  labelPrinter: string;
  /** A cash drawer plugged into the receipt printer. */
  drawerEnabled: boolean;
  drawerPin: '2' | '5' | 'both';
  drawerOnCash: boolean;
}

export const DEFAULT_SETTINGS: StoreSettings = {
  // A fresh install belongs to nobody yet. The demo seed names the demo shop;
  // a real one is named in Settings, and every screen falls back to a plain
  // "POS" until it is - better than wearing another shop's name.
  storeNameEn: '',
  storeNameAr: '',
  address: '',
  phone: '',
  taxNumber: '',
  logoDataUrl: null,
  vatRate: 16,
  cashStepAgorot: 10,
  receiptLang: 'both',
  receiptFooterEn: 'Exchange within 30 days with this receipt.',
  receiptFooterAr: 'الاستبدال خلال ٣٠ يوماً مع هذه الفاتورة.',
  exchangeWindowDays: 30,
  allowCashRefund: false,
  noReceiptLookbackDays: 90,
  enforceFinalSale: true,
  maxCashierDiscountPercent: 10,
  lowStockDefault: 3,
  theme: 'auto',
  accent: '#b4542e',
  deadStockDays: 60,
  customerTabsEnabled: true,
  shiftsEnabled: false,
  holdSalesEnabled: false,
  arabicDigits: false,
  unsyncedWarnHours: 6,
  idleSignOutMinutes: 0,
  setupDone: true,
  printVia: 'window',
  receiptPrinter: '',
  receiptPaper: '80',
  autoPrintReceipt: false,
  labelPrinter: '',
  drawerEnabled: false,
  drawerPin: '2',
  drawerOnCash: true,
  paymentMethods: [
    { key: 'cash', labelEn: 'Cash', labelAr: 'نقداً', kind: 'cash' },
    { key: 'card', labelEn: 'Card', labelAr: 'بطاقة', kind: 'card' },
    { key: 'credit', labelEn: 'Store credit', labelAr: 'رصيد المحل', kind: 'credit' },
    { key: 'gift', labelEn: 'Gift card', labelAr: 'بطاقة هدية', kind: 'gift' },
  ],
};
